import { Navbar, Alignment, Button as BlueprintButton, Tooltip as BlueprintTooltip } from '@blueprintjs/core';
import { isEmpty, isNil, uniqBy } from 'lodash';
import { PAINT_ICON } from 'polotno/toolbar/copy-style';
import { HistoryButtons } from 'polotno/toolbar/history-buttons';
import { useCopyStyle } from 'polotno/toolbar/use-copy-style';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
	Button,
	Container,
	Dropdown,
	Form,
	InputGroup,
	ListGroup,
	ProgressBar,
	Spinner,
	Collapse,
} from 'react-bootstrap';
import {
	BsQuestionCircle,
} from 'react-icons/bs';
import {
	getAssetLoadTimeout,
	setAssetLoadTimeout,
	whenLoaded,
} from 'polotno/utils/loader';
import BayAddButton from '../BayAddButton';
import BayCheckbox from '../BayCheckbox';
import BayLoader from '../BayLoader';
import BayModal from '../BayModal';
import MediaMetricsPills from '../BayPolotno/toolbars/MediaMetricsPills';
import BaySelect from '../BaySelect';
import BaySpinner from '../BaySpinner';
import BayToast from '../BayToast';
import BayTooltip from '../BayTooltip';
import type { UserTourConfig } from '../BayUserTour';
import { Photos } from './sections/Photos';
import {
	SECTION_ORDER_BY_EDITOR_MODE,
	SECTION_SORT_KEY_ALIASES,
} from './constants';
import {
	DESIGN_IMPORT_ACCEPT,
	IMPORT_ASSET_LOAD_TIMEOUT_MS,
	IMPORT_LOG_SUPPRESSION_MAX_MS,
} from './sections/constants';
import EditorHeader from '../DesignStudio/EditorHeader';
import { designExportOptions } from '../DesignStudio/EditorHeader/exportOptions';
import {
	canUseTemplateVideoFeatures,
	loadDesignFileToStore,
	storeHasVideoElements,
	storeHasAudioElements,
} from '../utilities';
import { isEmptyPage } from '../utilities/polotnoImport';

import {
	PDF_LIKE_IMPORT_KEYS,
	filterDesignImportOptions,
} from './importOptions';
import './BayPolotnoEditor.scss';

const statusOptions = [
	{ value: 'draft', label: 'Draft' },
	{ value: 'active', label: 'Active' },
];

interface PolotnoSection {
	name: string;
	Tab: React.ComponentType<any>;
	Panel: React.ComponentType<any>;
	[key: string]: any;
}

interface DesignImportOption {
	key: string;
	label: string;
	accept?: string;
	beta?: boolean;
}

const getSectionSortKey = (name: unknown): string => {
	if (typeof name !== 'string') return '';
	const normalizedName = name.toLowerCase();

	return (
		(SECTION_SORT_KEY_ALIASES as Record<string, string>)[normalizedName] ||
		normalizedName
	);
};

const sortSectionsByOrder = (
	sections: PolotnoSection[],
	orderedNames?: string[],
): PolotnoSection[] => {
	if (!orderedNames?.length) return sections;

	const orderMap = new Map(
		orderedNames.map((name, index) => [name.toLowerCase(), index]),
	);

	const fallbackRank = orderedNames.length;

	return [...sections].sort((a, b) => {
		const aRank = orderMap.get(getSectionSortKey(a?.name)) ?? fallbackRank;
		const bRank = orderMap.get(getSectionSortKey(b?.name)) ?? fallbackRank;

		return aRank - bRank;
	});
};

const sleep = (ms: number) =>
	new Promise<void>((resolve) => setTimeout(resolve, ms));

const isKnownPdfImportWarn = (warningText = '') => {
	return (
		warningText.includes('Failed to extract image') &&
		warningText.includes('Timeout waiting for image data')
	);
};

const isKnownPdfImportError = (errorText = '') => {
	return (
		errorText.includes(
			'Timeout triggered for loader. Some assets may not have loaded.',
		) || errorText.includes('Timeout loading asset')
	);
};

const getImportFileExtension = (fileName = '') => {
	const parts = String(fileName).toLowerCase().split('.');
	return parts.length > 1 ? parts.pop() || '' : '';
};

const DESIGN_IMPORT_OPTIONS: DesignImportOption[] = [
	{
		key: 'svg',
		label: 'SVG',
		accept: '.svg,image/svg+xml',
		beta: true,
	},
	// pdf/ai both convert through the same pdfToJson() path (see
	// utilities/polotnoImport.js) and are filtered out together below via
	// isPdfImportEnabled when that path's payload size is a problem.
	{
		key: 'pdf',
		label: 'PDF',
		accept: '.pdf,application/pdf',
		beta: true,
	},
	{
		key: 'ai',
		// "AI" reads as artificial intelligence to end users -- the file extension
		// stays `.ai`, only the label drops it.
		label: 'Adobe Illustrator',
		accept: '.ai',
		beta: true,
	},
];

export { PDF_LIKE_IMPORT_KEYS, filterDesignImportOptions };

const DESIGN_IMPORT_EVENT = 'bay-ui:design-import-request';

interface UnsplashPanelCaptionOverrideProps {
	PanelComponent: React.ComponentType<any>;
	panelProps: any;
}

// Custom wrapper for Unsplash-backed panels to override the default caption.
const UnsplashPanelCaptionOverride: React.FC<
	UnsplashPanelCaptionOverrideProps
> = ({ PanelComponent, panelProps }) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const caption = ref.current?.querySelector('p');

		if (caption) {
			caption.textContent = 'Background Images from ';
			const link = document.createElement('a');
			link.href = 'https://unsplash.com/';
			link.textContent = 'Unsplash';
			caption.appendChild(link);
		}
	}, []);

	return (
		<div ref={ref} className="h-100 overflow-auto d-flex flex-column">
			<PanelComponent {...panelProps} />
		</div>
	);
};

interface CopyStyleWithBottomTooltipProps {
	store: any;
}

const CopyStyleWithBottomTooltip: React.FC<CopyStyleWithBottomTooltipProps> = ({ store }) => {
	const { disabled, elementToCopy, setElementToCopy } = useCopyStyle(store);

	return (
		<BlueprintTooltip content="Copy style" disabled={disabled} placement="bottom">
			<BlueprintButton
				minimal
				active={!!elementToCopy}
				aria-label="Copy style"
				disabled={disabled}
				icon={PAINT_ICON}
				intent={elementToCopy ? 'primary' : 'none'}
				onClick={() => setElementToCopy(store.selectedElements[0])}
			/>
		</BlueprintTooltip>
	);
};

interface CategoriesListProps {
	categories: any[];
	selectedCategories: any[];
	onSelectCategory: (category: any) => void;
	isSaving: boolean;
}

const CategoriesList: React.FC<CategoriesListProps> = ({
	categories,
	selectedCategories,
	onSelectCategory,
	isSaving,
}) => {
	return (
		<ListGroup className="category-list-items" variant="flush">
			{categories?.map((category, index) => {
				return (
					<ListGroup.Item
						key={index}
						action
						active={
							selectedCategories.find((e) => e?.value === category?.value)
								? true
								: false
						}
						as="div"
						className="d-flex align-items-center p-1 px-2 cursor-pointer category-list-item"
						disabled={isSaving}
						onClick={() => onSelectCategory(category)}
					>
						<BayCheckbox
							isChecked={
								selectedCategories.find((e) => e?.value === category?.value)
									? true
									: false
							}
							name={`category_${index}`}
							onChangeFunc={() => onSelectCategory(category)}
						/>

						<div className="ms-2 flex-1 text-truncate">
							<span className="category-label">{category.label}</span>
						</div>

						<div className="text-700 ms-auto text-truncate">
							<span className="owner-label">
								{typeof category?.owner === 'string'
									? category.owner
									: category?.owner?.displayName || category?.owner?.name || ''}
							</span>
						</div>
					</ListGroup.Item>
				);
			})}
		</ListGroup>
	);
};

interface EditorLayoutProps {
	isLoading?: boolean;
	isSaving?: boolean;
	checkExit: (param?: any) => void;
	customers?: any[];
	handleExit: (save: boolean, param?: any) => void;
	handleSave: (param?: any) => void | Promise<void>;
	handleDownload: (exportType: string) => void;
	handleExportVideo?: () => void;
	handleNameChange: (name: string) => void;
	projectNameInputRef: React.RefObject<any>;
	projectName: string;
	projectStore: any;
	projectState: any;
	actingAsState?: any;
	setShowExitWarning: (show: boolean | any) => void;
	showExitWarning: boolean | any;

	setTemplateTags?: (tags: any[]) => void;
	setTemplateCategories?: (categories: any[]) => void;
	setStatus?: (status: string) => void;
	setTemplateOwner?: (owner: string | number | null, param?: boolean) => void;
	status?: string;
	showSaveDialog?: boolean;
	setShowSaveDialog?: (show: boolean) => void;
	templateOwner?: string | number | null;
	templateTags?: any[];
	templateCategories?: any[] | null;
	templateState?: any;

	formCategories?: any[];
	tagOptions?: any[];
	tagSetOptions?: any[];
	setIsCategoriesLoading?: (loading: boolean) => void;
	isCategoriesLoading?: boolean;
	handleSelectCategory?: (category: any) => void;
	handleCreateTemplateTag?: (params: any) => Promise<any>;

	getAccess: () => any;

	customSections?: any[];
	t: (key: string, fallback?: string | Record<string, unknown>) => string;
	defaultSections?: any[];
	polotnoContainerComponent: React.ComponentType<any>;
	sidePanelWrapComponent: React.ComponentType<any>;
	sidePanelComponent: React.ComponentType<any>;
	toolbarComponent: React.ComponentType<any>;
	zoomButtonsComponent: React.ComponentType<any>;
	workspaceWrapComponent: React.ComponentType<any>;
	workspaceComponent: React.ComponentType<any>;
	optionsComponent?: React.ComponentType<any>;
	pageControlsComponent: React.ComponentType<any>;
	pagesTimelineComponent?: React.ComponentType<any>;
	textFontFamilyComponent: React.ComponentType<any>;
	textFontVariantComponent: React.ComponentType<any>;
	fromSocial?: boolean;
	isDesignAdmin?: boolean;

	childrenActions?: React.ReactNode;
	shareButtonVariant?: 'compact' | undefined | null | false;
	shareToSocialDisabled?: boolean;
	shareToSocialTooltip?: string;
	projectNameError?: boolean;
	isVideoExporting?: boolean;
	hideHeader?: boolean;

	isListingOverlayValidationEnabled?: boolean;
	isListingOverlaySaveDisabled?: boolean;

	// Forwarded to EditorHeader; omitted -> BayUserTour falls back to window.BAY_USERTOUR
	userTour?: UserTourConfig;

	// Feature flags — computed by the consuming app and passed down
	isVideoEditingEnabled?: boolean;
	// Arrived from develop's untyped .jsx in the #876c7ff6 merge; declared here
	isPdfImportEnabled?: boolean;
	// Uploads one extracted asset and resolves to its URL. Absent, an imported
	// design keeps the base64 rasters the importer inlined.
	uploadAsset?: (file: File) => Promise<string>;
	// Page cap a PDF import can't push the design past. The consuming app owns
	// it; absent, imports are unlimited as before.
	importMaxPages?: number;
}

const EditorLayout: React.FC<EditorLayoutProps> = ({
	isLoading = false,
	isSaving = false,
	checkExit,
	customers = [],
	handleExit,
	handleSave,
	handleDownload,
	handleExportVideo,
	handleNameChange,
	projectNameInputRef,
	projectName,
	projectStore,
	projectState,
	actingAsState,
	setShowExitWarning,
	showExitWarning,

	setTemplateTags = () => {},
	setTemplateCategories = () => {},
	setStatus = () => {},
	setTemplateOwner = () => {},
	status = '',
	showSaveDialog = false,
	setShowSaveDialog = () => {},
	templateOwner = null,
	templateTags = [],
	templateCategories = null,
	templateState,

	formCategories = [],
	tagOptions = [],
	tagSetOptions = [],
	setIsCategoriesLoading = () => {},
	isCategoriesLoading = false,
	handleSelectCategory = () => {},
	handleCreateTemplateTag,

	getAccess,

	t,
	customSections = [],
	defaultSections = [],
	polotnoContainerComponent: PolotnoContainer,
	sidePanelWrapComponent: SidePanelWrap,
	sidePanelComponent: SidePanel,
	toolbarComponent: Toolbar,
	zoomButtonsComponent: ZoomButtons,
	workspaceWrapComponent: WorkspaceWrap,
	workspaceComponent: Workspace,
	optionsComponent: Options,
	pageControlsComponent: PageControls,
	pagesTimelineComponent: PagesTimeline,
	textFontFamilyComponent: TextFontFamily,
	textFontVariantComponent: TextFontVariant,
	fromSocial = false,
	isDesignAdmin = false,

	childrenActions,
	shareButtonVariant,
	shareToSocialDisabled = false,
	shareToSocialTooltip = '',
	projectNameError,
	isVideoExporting = false,
	hideHeader = false,

	isListingOverlayValidationEnabled = false,
	isListingOverlaySaveDisabled = false,

	userTour,

	// Feature flags — computed by the consuming app and passed down
	isVideoEditingEnabled = false,
	// PDF/AI design import inflates the saved project payload (every PDF
	// page is merged in as a full page with embedded image/vector data) and
	// can push a save past a consumer's backend payload-size limit. Default
	// on for back-compat; a consuming app that hits the limit passes false.
	// Both MD and client need PDF/AI import, so this stays a prop rather than
	// a per-platform branch in here -- each app decides against its own limit.
	isPdfImportEnabled = true,
	uploadAsset,
	importMaxPages,
}) => {
	const [templateStatus, setTemplateStatus] = useState(
		statusOptions.find((e) => e?.value === status) ?? statusOptions[0],
	);

	const [tagSetOption, setTagSetOption] = useState<any>('');
	const [tagValue, setTagValue] = useState('');
	const [isCollapsed, setIsCollapsed] = useState(true);
	const [statusOptionsArray, setStatusOptionsArray] = useState<any[]>([]);
	const [importToastData, setImportToastData] = useState<{
		show: boolean;
		variant: string;
		description: string;
	}>({
		show: false,
		variant: 'warning',
		description: '',
	});
	const [isImporting, setIsImporting] = useState(false);
	const [importLength, setImportLength] = useState(0);
	const importProgressRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const startImportProgress = () => {
		setImportLength(0);
		let current = 0;
		importProgressRef.current = setInterval(() => {
			current += current < 70 ? 3 : 0.5;
			if (current >= 90) {
				if (importProgressRef.current) clearInterval(importProgressRef.current);
				return;
			}
			setImportLength(Math.round(Math.min(current, 90)));
		}, 150);
	};

	const stopImportProgress = () => {
		if (importProgressRef.current) clearInterval(importProgressRef.current);
		setImportLength(100);
	};

	const [saveModalStep, setSaveModalStep] = useState(1);
	const [isFinalQualityChecked, setIsFinalQualityChecked] = useState(false);
	const designImportInputRef = useRef<HTMLInputElement>(null);

	const tagsInputRef = useRef<HTMLInputElement>(null);

	let defaultSection = 'brand';
	const isListingOverlayTemplate = Boolean(
		templateState?.etc?.isListingOverlay ||
		templateState?.etc?.cfg?.order?.itemId === 'listing-overlays',
	);

	const access = getAccess();
	const videoFeatureContext = templateState || projectState;
	const templateHasMedia =
		storeHasVideoElements(projectStore) || storeHasAudioElements(projectStore);
	const showVideoExportButton = Boolean(
		isVideoEditingEnabled &&
		(canUseTemplateVideoFeatures(videoFeatureContext) || templateHasMedia),
	);
	const HistoryWithMetrics = useMemo(
		() =>
			function HistoryWithMetricsComponent({ store }: any) {
				return (
					<Navbar.Group align={Alignment.LEFT} className="pe-2">
						<HistoryButtons store={store} />
						<MediaMetricsPills store={store} t={t} />
					</Navbar.Group>
				);
			},
		[t],
	);

	// Stable type on purpose: inline here, Polotno remounts PageControls every editor render and wipes its state (Smart Copy's toast).
	const workspaceComponents = useMemo(
		() => ({
			PageControls: (props: any) => <PageControls {...props} translate={t} />,
		}),
		[PageControls, t],
	);

	if (
		(!isDesignAdmin &&
			!isNil(projectState.jsonContext.listings) &&
			!isEmpty(projectState.jsonContext.listings)) ||
		projectState?.tagKeys?.find((e: string) => e === 'Category:Listing Marketing')
	) {
		defaultSection = 'listingimages';
	} else if (
		isListingOverlayTemplate &&
		Array.isArray(customSections) &&
		customSections.some((s) => s?.name === 'formats')
	) {
		defaultSection = 'formats';
	}

	const tagOptionsForSave =
		isListingOverlayTemplate && Array.isArray(tagOptions)
			? tagOptions.filter(
				(t) =>
					t && !(t.tagSet?.name === 'Type' &&['Email', 'Print Product'].includes(t.name)),
			)
			: tagOptions;

	const DEFAULT_SECTIONS_ARRAY = defaultSections.filter((section: any) => {
		if (
			section.name === 'templates' ||
			section.name === 'size' ||
			section.name === 'text'
		) {
			return false;
		}

		if (section.name === 'layers') {
			return isDesignAdmin;
		}

		return true;
	});

	const sidePanelLabelOverrides: Record<string, string> = {
		photos: 'Images',
		elements: 'Shapes',
	};

	const defaultSectionsArray: PolotnoSection[] = DEFAULT_SECTIONS_ARRAY.map(
		(section: PolotnoSection) => {
			const currentSection = section.name === 'photos' ? Photos : section;
			const labelOverride = sidePanelLabelOverrides[currentSection.name];

			return {
				...currentSection, // Spread the original section properties

				...(labelOverride && {
					Tab: (props: any) => (
						<currentSection.Tab {...props} name={labelOverride} />
					),
				}),

				...(currentSection.name === 'background' && {
					Panel: (panelProps: any) => (
						<UnsplashPanelCaptionOverride
							PanelComponent={currentSection.Panel}
							panelProps={panelProps}
						/>
					),
				}),
			};
		},
	);

	const mergedSections = [...customSections, ...defaultSectionsArray];
	const SECTIONS = sortSectionsByOrder(
		mergedSections,
		isDesignAdmin
			? SECTION_ORDER_BY_EDITOR_MODE.designManager
			: SECTION_ORDER_BY_EDITOR_MODE.designStudio,
	);

	const isFinalQualityStepEnabled = Boolean( isListingOverlayValidationEnabled );

	useEffect(() => {
		if (!showSaveDialog) return;

		setSaveModalStep(isFinalQualityStepEnabled ? 0 : 1);
		setIsFinalQualityChecked(false);
	}, [showSaveDialog, isFinalQualityStepEnabled]);

	useEffect(() => {
		if (access?.isGlobalRole) {
			const newStatusOptions = [
				...statusOptions,
				{ value: 'inactive', label: 'Inactive' },
				{ value: 'review', label: 'Review' },
				{ value: 'archived', label: 'Archived' },
			];

			setStatusOptionsArray(newStatusOptions);
		} else {
			setStatusOptionsArray(statusOptions);
		}

		return () => {
			if (isDesignAdmin && showSaveDialog === false) {
				resetModalState();
			}
		};
	}, [projectState, templateState]);

	const resetModalState = () => {
		setSaveModalStep(isFinalQualityStepEnabled ? 0 : 1);
		setIsFinalQualityChecked(false);
		setTemplateStatus(templateStatus);
		setTemplateOwner(templateOwner || '');
		setTemplateTags(templateTags);
		setTemplateCategories(templateCategories);
		setTagValue('');
		setTagSetOption('');
	};

	const handleFormChange = (name: string, valueObject: any) => {
		switch (name) {
		case 'template_owner':
			// Keep Back At You owner as numeric 0 because select values may arrive as string '0'.
			setTemplateOwner(
				valueObject?.value === '0' ? 0 : (valueObject?.value ?? null),
				true,
			);
			break;
		case 'template_status':
			setTemplateStatus(valueObject);
			setStatus(valueObject?.value);
			break;
		case 'template_category':
			handleSelectCategory(valueObject);
			break;
		case 'template_general_tags':
			setTemplateTags(valueObject);
			break;
		default:
			break;
		}
	};

	const handleContinue = (e: React.FormEvent, isCancelButton = false) => {
		e.preventDefault();
		const firstModalStep = isFinalQualityStepEnabled ? 0 : 1;

		switch (isCancelButton) {
		case true:
			if (saveModalStep === firstModalStep) {
				handleCancelButton();
			} else {
				setSaveModalStep((prevStep) => prevStep - 1);
			}
			break;

		default:
			if (saveModalStep === 0) {
				if (!isFinalQualityChecked) return;
				setSaveModalStep(1);
				return;
			}

			if (saveModalStep === 2) {
				handleSaveButton(e);
			} else {
				setSaveModalStep((prevStep) => prevStep + 1);
			}

			break;
		}
	};

	const handleCancelButton = () => {
		setIsFinalQualityChecked(false);
		setShowSaveDialog(false);
		resetModalState();
	};

	const handleSaveButton = async (e: React.FormEvent) => {
		e.preventDefault();

		await handleSave(e);
		resetModalState();
	};

	const handleTagValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setTagValue(e.target.value);
	};

	const handleTagAdd = async () => {
		setIsCategoriesLoading(true);

		if (!handleCreateTemplateTag) {
			setIsCategoriesLoading(false);
			return;
		}

		await handleCreateTemplateTag({
			tagSet: tagSetOption,
			tagName: tagValue,
			ownerId: templateOwner,
		})
			.then((resp) => {
				if (resp) {
					const uniqueTags = uniqBy(
						[
							...templateTags,
							{
								...resp,
								tagSet: tagSetOption,
								label: `${tagSetOption.label}:${resp?.name}`,
								value: resp?._id,
							},
						],
						'_id',
					);

					setTemplateTags(uniqueTags);
					setTagValue('');
				}
			})
			.catch((err) => {
				console.error(err);
			})
			.finally(() => {
				setIsCategoriesLoading(false);
			});
	};

	const getDefaultOwnerValue = (
		customers: any[],
		selectedOwner: string | number | null | undefined,
	) => {
		if (
			selectedOwner === undefined ||
			selectedOwner === null ||
			selectedOwner === '' ||
			customers?.length === 0
		) {
			return null;
		}

		const currentOwner = customers.find(
			(e) => String(e?._id) === String(selectedOwner),
		);

		return currentOwner
			? { ...currentOwner, label: currentOwner.name, value: currentOwner._id }
			: null;
	};

	const getOwnerOptions = (customers: any[]) => {
		return customers?.map((e) => ({ ...e, label: e.name, value: e._id }));
	};

	const CustomCloseModalFooter = ({
		handleExit,
		setShowExitWarning,
		isDesignAdmin,
		goBack,
	}: any) => {
		return (
			<>
				<Button
					variant="outline-secondary"
					onClick={() =>
						!isDesignAdmin
							? setShowExitWarning({ show: false })
							: setShowExitWarning(false)
					}
				>
					{t('cancel')}
				</Button>
				<div className="d-flex justify-content-end">
					<Button
						className="me-2 mb-0"
						variant="outline-primary"
						onClick={() => handleExit(false, true)}
					>
						{t('dontSave')}
					</Button>
					<Button
						variant="primary"
						onClick={() =>
							!isDesignAdmin
								? handleExit(true, goBack || false)
								: handleExit(true)
						}
					>
						{t('save')}
					</Button>
				</div>
			</>
		);
	};

	const CustomSaveTemplateModalFooter = ({
		handleSaveButton,
		handleCancelButton,
		handleContinue,
		isSuperAdmin,
		saveModalStep,
		isSaving,
		isDisabled,
		isListingOverlaySaveDisabled,
		isFinalQualityStepEnabled,
		isFinalQualityChecked,
	}: any) => {
		const isFinalQualityStep = isFinalQualityStepEnabled && saveModalStep === 0;
		const isActionDisabled = isFinalQualityStep
			? isSaving || isListingOverlaySaveDisabled || !isFinalQualityChecked
			: isDisabled || isSaving || isListingOverlaySaveDisabled;

		return (
			<>
				<Button
					disabled={isSaving}
					id="cancel_save_template_button"
					variant="outline-secondary"
					onClick={(e: React.MouseEvent) =>
						!isSuperAdmin ? handleCancelButton(e) : handleContinue(e, true)
					}
				>
					{isFinalQualityStep
						? t('continueEditing')
						: isSuperAdmin && saveModalStep > (isFinalQualityStepEnabled ? 0 : 1)
							? t('back')
							: t('cancel')}
				</Button>
				<div className="d-flex justify-content-end">
					<Button
						disabled={isActionDisabled}
						id="save_template_button"
						variant="primary"
						onClick={(e: React.MouseEvent) =>
							!isSuperAdmin ? handleSaveButton(e) : handleContinue(e)
						}
					>
						{isSaving ? (
							<span className="d-flex align-items-center">
								<Spinner className="me-2" size="sm" />
								<span>{t('saving')}</span>
							</span>
						) : isSuperAdmin && saveModalStep < 2 ? (
							t('continue')
						) : (
							t('save')
						)}
					</Button>
				</div>
			</>
		);
	};

	const CustomOption = (props: any) => (
		<div>
			<span className="fs-6 fw-semibold">{props.name}</span> <br />
			<span className="fs-7">
				ID Number: {props?.idNumber ?? 'No ID Number'}
			</span>
		</div>
	);

	const designImportOptions = filterDesignImportOptions(
		DESIGN_IMPORT_OPTIONS,
		isPdfImportEnabled,
	);

	const handleImportOptionSelect = (option?: DesignImportOption | null) => {
		// Reaching here with a gated key means a caller rendered an option this
		// gate hides -- the click would otherwise vanish with no trace. Warn
		// rather than return silently.
		if (!isPdfImportEnabled && option?.key && PDF_LIKE_IMPORT_KEYS.has(option.key)) {
			console.warn(
				`[BayPolotnoEditor] ignored "${option.key}" import: isPdfImportEnabled is false. ` +
					'Filter the Import menu through filterDesignImportOptions().',
			);
			return;
		}

		const input = designImportInputRef.current;
		if (!input) return;

		input.setAttribute('accept', option?.accept || DESIGN_IMPORT_ACCEPT);
		input.click();
	};

	useEffect(() => {
		const handleExternalImportRequest = (event: Event) => {
			handleImportOptionSelect((event as CustomEvent<DesignImportOption>)?.detail);
		};

		window.addEventListener(DESIGN_IMPORT_EVENT, handleExternalImportRequest);

		return () => {
			window.removeEventListener(
				DESIGN_IMPORT_EVENT,
				handleExternalImportRequest,
			);
		};
	}, []);

	const handleDesignImportInputChange = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const [file] = Array.from(event.target.files || []);

		if (!file) {
			event.target.value = '';
			return;
		}

		// A PDF appends pages; at the cap there's no room, so skip parsing it at all.
		// All-empty pages are filled by the import (see loadDesignFileToStore), so they don't count.
		if (importMaxPages && getImportFileExtension(file.name) === 'pdf') {
			const existingPages: any[] = projectStore.toJSON()?.pages || [];
			const usedPageCount = existingPages.every(isEmptyPage) ? 0 : existingPages.length;

			if (usedPageCount >= importMaxPages) {
				setImportToastData({
					show: true,
					variant: 'warning',
					description: t('importPageLimitReached', {
						defaultValue: 'This design already has the maximum of {{limit}} pages, so the PDF wasn\'t imported. Delete a page to make room.',
						limit: importMaxPages,
					}),
				});
				event.target.value = '';
				return;
			}
		}

		setIsImporting(true);
		startImportProgress();

		const extension = getImportFileExtension(file?.name);
		const isPdfLikeImport = extension === 'pdf' || extension === 'ai';
		const postImportSettleMaxWaitMs = isPdfLikeImport
			? IMPORT_LOG_SUPPRESSION_MAX_MS
			: 5000;

		const originalAssetLoadTimeout = getAssetLoadTimeout();
		const originalConsoleWarn = console.warn;
		const originalConsoleError = console.error;

		setAssetLoadTimeout(
			Math.max(originalAssetLoadTimeout, IMPORT_ASSET_LOAD_TIMEOUT_MS),
		);

		console.warn = (...args: any[]) => {
			const warningText = args
				.map((arg) => (typeof arg === 'string' ? arg : ''))
				.join(' ');

			if (isKnownPdfImportWarn(warningText)) {
				return;
			}

			originalConsoleWarn(...args);
		};

		console.error = (...args: any[]) => {
			const errorText = args
				.map((arg) => (typeof arg === 'string' ? arg : ''))
				.join(' ');

			if (isKnownPdfImportError(errorText)) {
				return;
			}

			originalConsoleError(...args);
		};

		try {
			const pageCountBefore = isPdfLikeImport ? projectStore.pages.length : 0;
			let skippedPageCount = 0;
			let pdfPageCount = 0;
			const importedJson = await loadDesignFileToStore({
				file,
				store: projectStore,
				uploadAsset,
				maxPages: importMaxPages,
				onPagesSkipped: (skipped, total) => {
					skippedPageCount = skipped;
					pdfPageCount = total;
				},
			});
			if (isPdfLikeImport) {
				// Empty pages are replaced rather than appended to, so the page count
				// before the import no longer locates the first imported page.
				const firstImportedId = importedJson?.pages?.[0]?.id;
				const firstImportedPage =
					projectStore.pages.find((page: any) => page.id === firstImportedId) ||
					projectStore.pages[pageCountBefore];
				if (firstImportedPage) {
					projectStore.selectPages([firstImportedPage.id]);
				}
			}
			setImportToastData(
				skippedPageCount > 0
					? {
						show: true,
						variant: 'warning',
						description: t('importPageLimitPartial', {
							defaultValue: 'Only {{imported}} of {{total}} PDF pages were imported. Designs can have up to {{limit}} pages.',
							imported: pdfPageCount - skippedPageCount,
							total: pdfPageCount,
							limit: importMaxPages,
						}),
					}
					: {
						show: true,
						variant: 'success',
						description: t(
							'importSuccess',
							'File imported successfully! Review your content layers to ensure all elements converted correctly.',
						),
					},
			);
		} catch (error) {
			console.error('Design import error:', error);
			// An asset that could not be uploaded rejects the whole import, so say
			// so rather than pointing the user at the file format.
			const isAssetFailure =
				(error as Error)?.name === 'DesignImportAssetError';
			setImportToastData({
				show: true,
				variant: 'warning',
				description: isAssetFailure
					? t(
						'importAssetUploadFailed',
						'We couldn\'t upload the images in this file, so it wasn\'t imported. Please try again.',
					)
					: t(
						'importFailed',
						'We couldn\'t import your file. Please check the format and try again.',
					),
			});
		} finally {
			await Promise.race([whenLoaded(), sleep(postImportSettleMaxWaitMs)]);
			stopImportProgress();
			await sleep(400);
			setIsImporting(false);
			setImportLength(0);
			console.warn = originalConsoleWarn;
			console.error = originalConsoleError;
			setAssetLoadTimeout(originalAssetLoadTimeout);
		}

		event.target.value = '';
	};

	return (
		<>
			<BayToast
				delay={3000}
				show={importToastData.show}
				variant={importToastData.variant}
				text={importToastData.description}
				onClose={() => setImportToastData((prev) => ({ ...prev, show: false }))}
			/>
			<input
				className="d-none"
				ref={designImportInputRef}
				accept={DESIGN_IMPORT_ACCEPT}
				type="file"
				onChange={handleDesignImportInputChange}
			/>
			{!hideHeader && <EditorHeader
				access={access}
				editorTitle={t('editor')}

				exportOptions={designExportOptions}
				isSaving={isSaving || (isLoading || isCategoriesLoading)}
				projectName={projectName}
				projectNameError={projectNameError}

				projectNameInputRef={projectNameInputRef}
				showStatus={isDesignAdmin}

				saveDisabled={isListingOverlaySaveDisabled}
				shareButtonVariant={shareButtonVariant}
				shareToSocialDisabled={shareToSocialDisabled}
				isLoading={isLoading}

				showCloseButton={(isDesignAdmin && fromSocial) || !isDesignAdmin}
				showVideoExportButton={showVideoExportButton}
				status={status}
				t={t}
				isVideoExporting={isVideoExporting}

				onBack={() => checkExit(true)}
				onClose={() =>
					!isDesignAdmin
						? setShowExitWarning({
							show: true,
							goBack: true,
						})
						: checkExit(fromSocial)
				}

				onExport={(exportType) => handleDownload(exportType)}
				isDesignAdmin={isDesignAdmin}

				onExportVideo={handleExportVideo}
				onNameChange={handleNameChange}
				shareToSocialTooltip={shareToSocialTooltip}

				showImportButton
				importOptions={designImportOptions}
				onImport={handleImportOptionSelect}
				importDisabled={isSaving || isLoading || projectNameError}

				userTour={userTour}

				onSave={(shareDesign?: any) => {
					if (isListingOverlaySaveDisabled) return;
					if (!isDesignAdmin) {
						handleSave(shareDesign);
						return;
					}
					setShowSaveDialog(true);
				}}
			>
				{childrenActions}
			</EditorHeader>}
			<Container
				fluid
				className={`${!isLoading ? 'p-0' : ''} position-relative`}
				style={{ height: hideHeader ? '100%' : 'calc(100vh - 60px)' }}
			>
				{!isLoading && (
					<>
						<PolotnoContainer
							className="designer-container bp5-dark"
							id="designer-container"
						>
							<SidePanelWrap>
								<SidePanel
									defaultSection={defaultSection}
									sections={SECTIONS}
									store={projectStore}
								/>
							</SidePanelWrap>
							<WorkspaceWrap>
								{!isDesignAdmin ? (
									<Toolbar
										components={{
											CopyStyle: CopyStyleWithBottomTooltip,
											TextFontVariant,
											TextFontFamily,
											History: HistoryWithMetrics,
										}}
										store={projectStore}
									/>
								) : (
									<Toolbar
										components={{
											ActionControls: Options,
											CopyStyle: CopyStyleWithBottomTooltip,
											TextFontVariant,
											TextFontFamily,
											Admin: () => null,
											History: HistoryWithMetrics,
										}}
										store={projectStore}
									/>
								)}
								<Workspace
									backgroundColor="#E8E8E8"
									bleedColor="rgba(255, 0, 0, 0.2)"
									components={workspaceComponents}
									pageBorderColor="rgba(0, 0, 0, 0.175)"
									renderOnlyActivePage={isListingOverlayTemplate}
									store={projectStore}
								/>
								<ZoomButtons store={projectStore} />
								{PagesTimeline ? (
									<PagesTimeline
										globalState={{
											useTemplateState: () => templateState,
											useActingAsState: () => actingAsState || access,
											useProjectState: () => projectState,
										}}
										isVideoEditingEnabled={isVideoEditingEnabled}
										store={projectStore}
										t={t}
									/>
								) : null}
							</WorkspaceWrap>
						</PolotnoContainer>

						{isSaving && (
							<div className="is-saving position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
								<BayLoader />
							</div>
						)}

						{isImporting && (
							<div className="import-loader-blur-backdrop position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
								<div
									className="bg-white rounded-2 shadow p-7 d-inline-flex flex-column align-items-center"
								>
									<ProgressBar
										className="mb-3 import-loader-progress-bar"
										now={importLength || 5}
									/>
									<p className="mb-0 fw-semibold text-nowrap text-center text-body-secondary">
										Importing your design! Translating your artwork into fully
										customizable layers...
									</p>
								</div>
							</div>
						)}
					</>
				)}

				{isLoading && (
					<div className="editor-loader d-flex flex-column position-relative align-items-center justify-content-center">
						<BayLoader />
					</div>
				)}
			</Container>

			<BayModal
				closeButton
				backdrop="static"
				footer={
					<CustomCloseModalFooter
						goBack={(showExitWarning as any)?.goBack}
						handleExit={handleExit}
						isDesignAdmin={isDesignAdmin}
						setShowExitWarning={setShowExitWarning}
					/>
				}
				id="exit_warning_modal"
				show={!isDesignAdmin ? (showExitWarning as any).show : showExitWarning}
				title={t('exitWarningTitle')}
				translate={t}
				onHideFunc={() =>
					!isDesignAdmin
						? setShowExitWarning({ show: false })
						: setShowExitWarning(false)
				}
			>
				<p>{t('exitWarning')}</p>
			</BayModal>

			{isDesignAdmin && showSaveDialog && (
				<BayModal
					backdrop="static"
					bodyClass="pt-1"
					closeButton={!isSaving}
					footer={
						<CustomSaveTemplateModalFooter
							handleCancelButton={handleCancelButton}
							handleContinue={handleContinue}
							handleSaveButton={handleSaveButton}
							isDisabled={
								isSaving ||
								isCategoriesLoading ||
								templateCategories === null ||
								(access.isGlobalRole &&
									(templateOwner === null || templateOwner === ''))
							}
							isFinalQualityChecked={isFinalQualityChecked}
							isFinalQualityStepEnabled={isFinalQualityStepEnabled}
							isListingOverlaySaveDisabled={isListingOverlaySaveDisabled}
							isSaving={isSaving}
							isSuperAdmin={access?.isGlobalRole}
							saveModalStep={saveModalStep}
						/>
					}
					height="100%"
					id="save_template_modal"
					show={showSaveDialog}
					title={saveModalStep === 0 ? t('finalCheckTitle') : t('saveTemplateTitle')}
					translate={t}
					onHideFunc={() => {
						setIsFinalQualityChecked(false);
						setShowSaveDialog(false);
						resetModalState();
					}}
				>
					{isCategoriesLoading ? (
						<div
							className="d-flex justify-content-center"
							style={{ minHeight: 220 }}
						>
							<BaySpinner />
						</div>
					) : (
						<>
							{saveModalStep !== 0 && <p>{t('saveTemplateContent')}</p>}

							{saveModalStep === 0 && (
								<>
									<div className="listing-overlay-content-text text-body fs-6 lh-base mb-3">
										<p className="mb-2">{t('finalCheckIntro')}</p>
										<p className="mb-2">{t('finalCheckWatch')}</p>
										<ul className="mb-0 ps-3">
											<li className="mb-1">
												<strong>{t('finalCheckDynamicTitle')}</strong>{' '}
												{t('finalCheckDynamicBody')}
											</li>
											<li className="mb-1">
												<strong>{t('finalCheckBrandTitle')}</strong>{' '}
												{t('finalCheckBrandBody')}
											</li>
											<li className="mb-1">
												<strong>{t('finalCheckBleedTitle')}</strong>{' '}
												{t('finalCheckBleedBody')}
											</li>
											<li className="mb-0">
												<strong>{t('finalCheckOverlapTitle')}</strong>{' '}
												{t('finalCheckOverlapBody')}
											</li>
										</ul>
									</div>
									<div
										className="listing-overlay-content-text form-check text-body fs-6 lh-base mb-0"
									>
										<input
											checked={isFinalQualityChecked}
											className="form-check-input"
											id="final_quality_check_confirmed"
											type="checkbox"
											onChange={(event) =>
												setIsFinalQualityChecked(Boolean(event?.target?.checked))
											}
										/>
										<label className="form-check-label" htmlFor="final_quality_check_confirmed">
											{t('finalCheckConfirmLabel')}
										</label>
									</div>
								</>
							)}

							<Form
								className={`scrollbar-sm pe-3 ${
									formCategories.length <= 0 || saveModalStep === 2 || saveModalStep === 0
										? 'overflow-y-visible'
										: ''
								}`}
								id="save_template_form"
								onSubmit={(e) => handleSave(e)}
							>
								{saveModalStep === 1 && (
									<>
										{access?.isGlobalRole && (
											<Form.Group className="py-2">
												<Form.Label aria-required htmlFor="templateOwner">
													{t('owner')}
												</Form.Label>
												<BaySelect
													customTemplate={CustomOption}
													controlledValue={getDefaultOwnerValue(
														customers,
														templateOwner,
													)}
													disabled={isSaving || customers?.length === 0}
													id="templateOwner"
													name="template_owner"
													optionsArray={getOwnerOptions(
														customers,
													)}
													onChangeFunc={(value) =>
														handleFormChange('template_owner', value)
													}
												/>
												<Form.Text muted className="d-block mt-2 ps-1">
													{t('additionalOwnersHint')}
												</Form.Text>
												{(templateOwner === null || templateOwner === '') && (
													<div className="invalid-feedback d-block">
														{t('required')}
													</div>
												)}
											</Form.Group>
										)}

										<Form.Group className="py-2">
											<Form.Label>{t('templateStatus')}</Form.Label>
											<BaySelect
												controlledValue={templateStatus}
												disabled={isSaving}
												isClearable={false}
												name="template_status"
												optionsArray={statusOptionsArray}
												onChangeFunc={(value) =>
													handleFormChange('template_status', value)
												}
											/>
										</Form.Group>

										{((templateOwner !== null && templateOwner !== '') ||
											(!access.isGlobalRole &&
												(templateOwner === null || templateOwner === ''))) &&
											formCategories?.length > 0 && (
											<Form.Group
												className="py-2 mb-2"
												disabled={isSaving}
												id="categories_list"
											>
												<div
													className="d-flex align-items-center justify-content-between border-bottom mb-2"
													id="categories_list_header"
												>
													<Form.Label>{t('allCategories')}</Form.Label>

													<Form.Label>{t('categoryOwner')}</Form.Label>
												</div>

												{formCategories?.length > 0 && (
													<CategoriesList
														categories={formCategories}
														isSaving={isSaving}
														selectedCategories={templateCategories || []}
														onSelectCategory={(category) =>
															handleFormChange('template_category', category)
														}
													/>
												)}
											</Form.Group>
										)}
									</>
								)}

								{access?.isGlobalRole && saveModalStep === 2 && (
									<>
										<Form.Group className="py-2">
											<Form.Label>
												<span className="me-2">{t('generalTags')}</span>
												<BayTooltip
													overlay={t('generalTagsOverlay')}
													placement="top"
												>
													<BsQuestionCircle />
												</BayTooltip>
											</Form.Label>

											<BaySelect
												isClearable
												allowSearch={false}
												allowSelectAll={false}
												classNamePrefix="bay-tags-select"
												closeMenuOnSelect={false}
												controlledValue={templateTags}
												createable={false}
												disabled={isSaving || tagOptions.length === 0}
												hideSelectedOptions
												isMulti
												name="template_general_tags"
												optionsArray={tagOptionsForSave}
												placeholder="Select..."
												tabSelectsValue={false}
												onChangeFunc={(value) =>
													handleFormChange('template_general_tags', value)
												}
											/>
											<Form.Text muted className="d-block mt-2 ps-1">
												{t('additionalTagsHint')}
											</Form.Text>
											<BayAddButton
												className="mt-2 p-1"
												disabled={isSaving}
												showIcon={isCollapsed}
												size="sm"
												text={
													isCollapsed
														? `${t('create')} ${t('tags')}`
														: 'Hide Input'
												}
												variant="link"
												onClick={() => setIsCollapsed(!isCollapsed)}
											/>
										</Form.Group>

										<Collapse in={!isCollapsed}>
											<Form.Group className="py-2 mb-2">
												<Form.Label htmlFor="templateTagsInput">
													{t('create')} {t('tags')}
												</Form.Label>
												<InputGroup>
													<Dropdown>
														<Dropdown.Toggle
															className="border"
															disabled={isSaving}
															id="tagset_dropdown"
															variant="light"
														>
															{tagSetOption?.label || 'Select'}
														</Dropdown.Toggle>
														<Dropdown.Menu
															className="scrollbar-sm"
															id="tagset_dropdown_menu"
														>
															{tagSetOptions?.map((tagSet, index) => (
																<Dropdown.Item
																	key={index}
																	onClick={() => setTagSetOption(tagSet)}
																>
																	{tagSet.label}
																	{` (${tagSet.owner.displayName})`}
																</Dropdown.Item>
															))}
														</Dropdown.Menu>
													</Dropdown>
													<Form.Control
														ref={tagsInputRef}
														disabled={isSaving}
														id="templateTagsInput"
														value={tagValue}
														onChange={handleTagValueChange}
													/>
													<BayAddButton
														className="border fw-normal"
														disabled={
															tagValue === '' || tagSetOption === '' || isSaving
														}
														text={t('create')}
														variant="outline-primary"
														onClick={handleTagAdd}
													/>
												</InputGroup>
											</Form.Group>
										</Collapse>
									</>
								)}
							</Form>
						</>
					)}
				</BayModal>
			)}
		</>
	);
};

export default EditorLayout;
